import Head from "next/head";
import { generateNextSeo } from "next-seo/pages";
import { buildCanonicalUrl } from "lib/seo";
import React from "react";

import {
  fetchNavbarAndFooter,
  fetchPaywallByIdServer,
} from "lib/fetchers/common";
import type { LeaderboardPage as LeaderboardPageType } from "lib/types/leaderboards";

import Navigation from "components/navigation/navigation";
import ErrorBoundary from "components/errorBoundary/errorBoundary";
import PreviewModeBanner from "components/previewModeBanner/previewModeBanner";
import Footer from "components/footer/footer";

import LeaderboardDetailView from "content-ui/leaderboards/leaderboardDetailView";
import { findPageBySlug } from "content-ui/leaderboards/fetcher";
import { makeDecisionServer } from "lib/zephr/public-api";
import { PaywallWindow } from "lib/types/paywalls";
import { DecisionAction } from "lib/zephr/types";
import { ClientOnlyPaywall } from "components/paywallModal/clientOnlyPaywall";
import {
  paywallLeaderboard,
  stripRevenueData,
} from "content-ui/leaderboards/paywallLeaderboard";

export async function getServerSideProps({
  req,
  res,
  params,
  preview = false,
}) {
  const dato = await findPageBySlug({ slug: params.slug[0], preview });

  const isInReview = dato.leaderboard?.publishState === "In review";
  if (!dato || !dato.leaderboard || (isInReview && !preview)) {
    return { notFound: true };
  }

  const { leaderboard } = dato;
  const blaizeSession = req.cookies["blaize_session"] || "";
  const isProLeaderboard = leaderboard.isPro ?? true;
  const decision = await makeDecisionServer(
    blaizeSession,
    "leaderboards-sdk",
    {
      is_pro: isProLeaderboard,
      slug: leaderboard.slug,
      path: req?.url,
    },
    req
  );

  // Revenue is Pro-only on every leaderboard, so ask leaderboards-sdk as if
  // this one were Pro: Zephr answers NONE to that for Pro readers. On a Pro
  // leaderboard the decision above already asked exactly that.
  const revenueDecision = isProLeaderboard
    ? decision
    : await makeDecisionServer(
        blaizeSession,
        "leaderboards-sdk",
        { is_pro: true, slug: leaderboard.slug, path: req?.url },
        req
      );

  const downloadDecision = await makeDecisionServer(
    blaizeSession,
    "leaderboards-data-download-sdk",
    {
      content_type: "leaderboard-download",
    },
    req
  );

  const [menuItems, paywall, downloadPaywall] = await Promise.all([
    fetchNavbarAndFooter(),
    decision !== undefined &&
    decision?.action === DecisionAction.DISPLAY_PAYWALL
      ? fetchPaywallByIdServer(decision?.payload?.paywallId)
      : null,
    downloadDecision !== undefined &&
    downloadDecision?.action === DecisionAction.DOWNLOAD_PAYWALL
      ? fetchPaywallByIdServer(downloadDecision?.payload?.paywallId)
      : null,
  ]);

  res.setHeader(
    "Cache-Control",
    "public, s-maxage=10, stale-while-revalidate=59"
  );

  // in review leaderboards available only by preview mode from Dato
  if (!dato || !dato.leaderboard || (isInReview && !preview)) {
    return { notFound: true };
  }

  // Only an explicit NONE counts as Pro, so a failed decision strips revenue
  // rather than leaking it.
  const isProUser = revenueDecision?.action === DecisionAction.NONE;
  const visibleLeaderboard =
    paywall === null ? leaderboard : paywallLeaderboard(leaderboard);

  return {
    props: {
      paywall,
      downloadPaywall,
      dato: isProUser
        ? visibleLeaderboard
        : stripRevenueData(visibleLeaderboard),
      slug: params.slug[0],
      canonical: buildCanonicalUrl(`/leaderboards/${params.slug[0]}`),
      blur: decision?.action !== DecisionAction.NONE,
      previewMode: preview,
      __initialState: {
        sitewideOption: { ...menuItems },
      },
    },
  };
}

type LeaderboardPageProps = {
  paywall: PaywallWindow | null;
  downloadPaywall: PaywallWindow | null;
  dato: LeaderboardPageType;
  previewMode: boolean;
  slug: string;
  canonical: string;
  blur: boolean;
};

const LeaderboardPage = (props: LeaderboardPageProps) => {
  const { dato, blur } = props;
  return (
    <>
      <Head>
        {generateNextSeo({
          canonical: props.canonical,
          openGraph: {
            title: dato.seo.title,
            description: dato.seo.description,
            images: [{ url: dato.seo.image.url }],
          },
          twitter: {
            handle: "@siftedeu",
            site: "@siftedeu",
            cardType: "summary_large_image",
          },
        })}
        <link rel="icon" href="/favicon.ico" />
      </Head>
      <Navigation />
      {props.previewMode ? (
        <PreviewModeBanner exitSlug={`/leaderboards/${props.slug}`} />
      ) : null}
      <ErrorBoundary>
        <>
          <LeaderboardDetailView
            blur={blur}
            leaderboard={dato}
            downloadPaywall={props.downloadPaywall}
          />
          {props.paywall !== null && (
            <ClientOnlyPaywall paywall={props.paywall!} />
          )}
        </>
      </ErrorBoundary>
      <Footer />
    </>
  );
};

export default LeaderboardPage;
